import type { ParameterTimeSeriesConfiguration } from "@/lib/api";

/** Viewer chart kind derived from {@link ModelMapVisualizationType}. */
export enum GraphType {
  LineGraph = "line_graph",
  Histogram = "histogram",
  Scatterplot = "scatterplot",
  VectorPlot = "vector_plot",
  AvaIndex = "ava_index",
  Pasquill = "pasquill",
}

/** Stored in widget JSON; matches backend `visualization_type` strings. */
export const MODEL_MAP_VISUALIZATION_TYPES = [
  "line_graph",
  "scatterplot",
  "histogram",
  "arrow_series",
  "ava_index",
  "pasquill",
] as const;

export type ModelMapVisualizationType = (typeof MODEL_MAP_VISUALIZATION_TYPES)[number];

/**
 * Units not listed only support line graphs (previous default for “normal” quantities).
 * Order is default preference when coercing an invalid or missing stored value.
 */
const ALLOWED_VISUALIZATION_BY_UNIT_CODE: Record<string, ModelMapVisualizationType[]> = {
  degree: ["scatterplot", "arrow_series"],
  foot: ["scatterplot"],
  okta: ["histogram"],
};

const DEFAULT_ALLOWED: ModelMapVisualizationType[] = ["line_graph"];

/**
 * AVA index parameters are unitless; do not key their chart off `unitless`.
 * Match the family (`harmonie-nl-ava-index-atm`, later model prefixes).
 */
export function isAvaIndexParameter(parameterId: string, unitCode?: string): boolean {
  if (unitCode !== undefined && unitCode !== "unitless") return false;
  return parameterId.includes("ava-index");
}

/**
 * Pasquill stability class parameters are unitless like the AVA index, so they
 * are matched on the id family (`harmonie-nl-pasquill-stability-class-atm`,
 * later model prefixes) rather than on `unitless`, which is a shared bucket.
 */
export function isPasquillParameter(parameterId: string, unitCode?: string): boolean {
  if (unitCode !== undefined && unitCode !== "unitless") return false;
  return parameterId.includes("pasquill");
}

export function seriesParameterIds(
  config: Pick<ParameterTimeSeriesConfiguration, "series">,
): string[] {
  return config.series.map((series) => series.parameter_id);
}

function seriesContainsAvaIndex(
  parameterIds: readonly string[] | undefined,
  unitCode?: string,
): boolean {
  return (parameterIds ?? []).some((id) => isAvaIndexParameter(id, unitCode));
}

function seriesContainsPasquill(
  parameterIds: readonly string[] | undefined,
  unitCode?: string,
): boolean {
  return (parameterIds ?? []).some((id) => isPasquillParameter(id, unitCode));
}

/** Treats empty or whitespace as missing (legacy or bad payloads). */
export function readVisualizationType(
  config: ParameterTimeSeriesConfiguration,
): string | undefined {
  const raw = config.visualization_type;
  if (typeof raw !== "string" || !raw.trim()) return undefined;
  return raw;
}

export function isModelMapVisualizationType(
  value: string | undefined,
): value is ModelMapVisualizationType {
  return (
    value !== undefined &&
    (MODEL_MAP_VISUALIZATION_TYPES as readonly string[]).includes(value)
  );
}

export function getAllowedVisualizationTypes(
  unitCode: string | undefined,
  parameterIds?: readonly string[],
): ModelMapVisualizationType[] {
  // AVA has a dedicated chart (value-colored bars plus a line); it is the only
  // option so groups saved before it existed coerce over from `line_graph`.
  if (seriesContainsAvaIndex(parameterIds, unitCode)) return ["ava_index"];
  // Pasquill is a class index, not a quantity: it renders as a block timeline,
  // so no chart style is offered alongside it.
  if (seriesContainsPasquill(parameterIds, unitCode)) return ["pasquill"];
  if (!unitCode) return DEFAULT_ALLOWED;
  return ALLOWED_VISUALIZATION_BY_UNIT_CODE[unitCode] ?? DEFAULT_ALLOWED;
}

/** First allowed type for the unit (CMS default and coercion fallback). */
export function defaultVisualizationForUnit(
  unitCode: string | undefined,
  parameterIds?: readonly string[],
): ModelMapVisualizationType {
  return getAllowedVisualizationTypes(unitCode, parameterIds)[0];
}

/**
 * If `saved` is a valid enum member for this unit, return it; otherwise the first allowed type.
 */
export function coerceVisualizationType(
  saved: string | undefined,
  unitCode: string | undefined,
  parameterIds?: readonly string[],
): ModelMapVisualizationType {
  const allowed = getAllowedVisualizationTypes(unitCode, parameterIds);
  if (
    saved !== undefined &&
    isModelMapVisualizationType(saved) &&
    allowed.includes(saved)
  ) {
    return saved;
  }
  return allowed[0];
}

/**
 * Only line graphs can render threshold bands/lines, so thresholds are offered
 * (and persisted) solely for that presentation. Other types (scatter, histogram,
 * arrows) drop any leftover thresholds.
 */
export function visualizationSupportsThresholds(
  visualizationType: string | undefined,
): boolean {
  return visualizationType === GraphType.LineGraph;
}

export function visualizationTypeToGraphType(v: ModelMapVisualizationType): GraphType {
  switch (v) {
    case "line_graph":
      return GraphType.LineGraph;
    case "scatterplot":
      return GraphType.Scatterplot;
    case "histogram":
      return GraphType.Histogram;
    case "arrow_series":
      return GraphType.VectorPlot;
    case "ava_index":
      return GraphType.AvaIndex;
    case "pasquill":
      return GraphType.Pasquill;
    default: {
      const _exhaustive: never = v;
      return _exhaustive;
    }
  }
}
